import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useVerification } from './useVerification';
import VerifyMeta from './VerifyMeta';
import { logScanEvent, ResolutionPath } from './logScanEvent';
import { accentFor } from './theme';
import VerifyHeader from './VerifyHeader';
import VerifyIdentity from './VerifyIdentity';
import VerifyOwnership from './VerifyOwnership';
import VerifyProtocolState from './VerifyProtocolState';
import VerifyAttribution from './VerifyAttribution';
import VerifyCTA from './VerifyCTA';
import VerifySkeleton from './VerifySkeleton';
import { formatNodeAttribution } from './nodeCodes';

/**
 * Public TTR verification page â€” /sku/:ttrCode.
 * Renders WITHIN the existing Schmidt app shell (global <Header/> from App.tsx).
 * No auth required; reads the anonymous publicVerification mirror.
 */

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="max-w-xl mx-auto">{children}</div>
);

const Footer: React.FC = () => (
  <p className="text-center text-xs text-gray-400 mt-6">
    Schmidt Auctions Â· PA Licensed Auctioneer AU006176 Â· Powered by AuctionSAAS
  </p>
);

// Shared "what to do next" actions for the error/empty states â€” keep branding,
// always offer a way forward.
const ErrorActions: React.FC<{ showRefresh?: boolean }> = ({ showRefresh }) => (
  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
    {showRefresh && (
      <button
        onClick={() => window.location.reload()}
        className="min-h-[44px] px-5 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
      >
        Check again
      </button>
    )}
    <Link
      to="/browse"
      className="min-h-[44px] flex items-center justify-center px-5 rounded-md border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
    >
      Browse auctions
    </Link>
    <a
      href="https://schmidtauctions.com"
      className="min-h-[44px] flex items-center justify-center px-5 rounded-md border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
    >
      Schmidt Auctions home
    </a>
  </div>
);

const ErrorCard: React.FC<{
  title: string;
  body: string;
  code?: string | null;
  showRefresh?: boolean;
}> = ({ title, body, code, showRefresh }) => (
  <>
    <div className="bg-white border border-gray-200 rounded-lg px-6 py-10 text-center">
      <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      <p className="text-gray-600 mt-2 text-sm max-w-sm mx-auto">{body}</p>
      {code && <p className="text-xs text-gray-400 mt-4 font-mono break-all">{code}</p>}
      <ErrorActions showRefresh={showRefresh} />
    </div>
    <Footer />
  </>
);

const VerifyPage: React.FC = () => {
  const { ttrCode } = useParams<{ ttrCode: string }>();
  const { status, doc, scanned, matchType } = useVerification(ttrCode);

  // Fire-and-forget scan logging â€” exactly once per resolved load.
  const logged = useRef(false);
  useEffect(() => {
    if (logged.current) return;
    if (status === 'loading' || status === 'error') return; // skip while pending / on read failure
    const path: ResolutionPath =
      status === 'ok' ? (matchType === 'fallback' ? 'fallback' : 'exact') : (status as ResolutionPath);
    logged.current = true;
    logScanEvent(ttrCode, scanned, path);
  }, [status, matchType, scanned, ttrCode]);

  if (status === 'loading') {
    return <Shell><VerifySkeleton /></Shell>;
  }

  if (status === 'invalid') {
    return (
      <Shell>
        <ErrorCard
          title="We can't read that TTR code"
          body="The code in this link doesn't look right. Re-scan the QR code on the sticker, or double-check the link you opened."
        />
      </Shell>
    );
  }

  if (status === 'error') {
    return (
      <Shell>
        <ErrorCard
          title="Verification temporarily unavailable"
          body="We couldn't reach the registry just now. This is usually momentary â€” please try again."
          code={scanned ? scanned.ttrCode : null}
          showRefresh
        />
      </Shell>
    );
  }

  if (status === 'not_found' || !doc || !scanned) {
    return (
      <Shell>
        <ErrorCard
          title="This TTR code is being processed"
          body="This item is registered, but its public record is still being prepared. Check back in a moment."
          code={scanned ? scanned.ttrCode : null}
          showRefresh
        />
      </Shell>
    );
  }

  // â”€â”€ Full verification render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const accent = accentFor(doc.display.boardCategory);
  // Node attribution from the SCANNED node token (SCH / legacy SCHMIDT), falling
  // back to the mirror doc's holder name for unknown nodes.
  const attribution = formatNodeAttribution(scanned.holder, doc.ownership.holderName);

  return (
    <Shell>
      <VerifyMeta doc={doc} scanned={scanned} rawTtrCode={ttrCode} />
      <VerifyHeader boardName={doc.display.boardName} accent={accent} />
      <VerifyIdentity display={doc.display} scanned={scanned} accent={accent} />
      <VerifyOwnership ownership={doc.ownership} />
      <VerifyAttribution attribution={attribution} />
      <VerifyProtocolState protocolState={doc.protocolState} accent={accent} />

      {/* TTR Verified Â· Provenance Chain */}
      <div className="border-x border-gray-200 px-5 py-4 bg-white">
        <p className="text-sm text-gray-700">
          This item is registered in The Collectors Guild protocol.
        </p>
        <p className="text-xs uppercase tracking-wide text-gray-400 mt-3 font-semibold">
          TTR Verified Â· Provenance Chain
        </p>
        <p className="text-xs text-gray-600 mt-1 break-all">
          Provenance verified Â· TTR code{' '}
          <span className="font-mono text-gray-900">{scanned.ttrCode}</span>
        </p>
      </div>

      <VerifyCTA doc={doc} scanned={scanned} />
      <Footer />
    </Shell>
  );
};

export default VerifyPage;
